import { IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  GridToolbar,
} from "@mui/x-data-grid";
import { useContext, useEffect, useState } from "react";
import {
  AlertComponent,
  ModalComponent,
  NavBar,
  ProductForm,
  ShowContent,
  Spinner,
} from "../../components";
import DataContext from "../../context/DataContext";
import PaginatorContext from "../../context/PaginatorContext";
import { useFetch } from "../../hooks";
import { ProductProps } from "../../interfaces/interfaces";
import styles from "../main.module.sass";
import EditIcon from "@mui/icons-material/Edit";
import { initial_tool_state } from "../../utils/data";
import AddBoxIcon from "@mui/icons-material/AddBox";
import {
  modernDataGridStyles,
  modernIconButtonStyles,
} from "../../utils/dataGridStyles";

interface FetchResponse {
  success?: boolean;
  data: ProductProps[];
}

const initialProductState: ProductProps = {
  id: 0,
  partNumber: "",
  name: "",
  description: "",
  supplier: "",
  topThreadConnection: "",
  bottomThreadConnection: "",
  status: { id: 0, name: "" },
  maxOD: "",
  bodyOD: "",
  length: 0,
  quantity: 1,
  weight: 0,
  osi: false,
  tailJoint: false,
  espAssembly: false,
  imagePath: null,
  size: 0,
  isCompound: false,
};

export default function ProductPage() {
  const theme = useTheme();
  const { productOptions, fetchDataProducts, isSuccess, isError, isLoading } =
    useContext(DataContext);
  const { paginationModel, fetchPaginationModel } =
    useContext(PaginatorContext);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [productInfo, setProductInfo] =
    useState<ProductProps>(initialProductState);
  const matches = useMediaQuery("(min-width:600px)");

  const { pagination } = useFetch<FetchResponse>("product", paginationModel);

  useEffect(() => {
    fetchDataProducts();
  }, [fetchDataProducts]);

  const handleEditProduct = (product: ProductProps) => {
    setProductInfo(product);
    setIsOpenModal(true);
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      renderHeader: () => <strong>{"NAME"}</strong>,
      width: 300,
    },
    {
      field: "price",
      headerName: "Price",
      renderHeader: () => <strong>{"PRICE"}</strong>,
      width: 100,
      valueGetter: (params: GridValueGetterParams) =>
        "$" + params.row?.price || "",
    },
    {
      field: "category",
      headerName: "Category",
      renderHeader: () => <strong>{"CATEGORY"}</strong>,
      width: 250,
    },
    {
      field: "description",
      headerName: "Description",
      renderHeader: () => <strong>{"DESCRIPTION"}</strong>,
      width: 400,
    },
    {
      field: "action",
      headerName: "Actions",
      renderHeader: () => <strong>{"ACTIONS"}</strong>,
      width: 150,
      filterable: false,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <div className="action-buttons">
            <Tooltip title="Edit">
              <IconButton
                onClick={() => handleEditProduct(params.row)}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const buttons = [
    {
      title: "New",
      action: () => {
        setProductInfo(initial_tool_state);
        setIsOpenModal(true);
      },
      icon: <AddBoxIcon />,
    },
  ];

  let content: JSX.Element | JSX.Element[];

  if (isLoading) {
    content = <Spinner />;
  } else {
    content = (
      <>
        <NavBar title="Products" buttons={buttons} />
        <div className={styles.center}>
          <div style={{ height: "75vh" }} className={styles.techProposalForm}>
            <DataGrid
              density={matches ? "standard" : "compact"}
              sx={modernDataGridStyles(theme)}
              rows={productOptions}
              columns={columns}
              slots={{ toolbar: GridToolbar }}
              className={styles.dataTable}
              loading={isLoading}
              rowCount={+pagination?.totalRecords}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={fetchPaginationModel}
            />
          </div>
          {isSuccess && <AlertComponent type="success" />}
          {isError && <AlertComponent type="error" />}
        </div>

        {isOpenModal && (
          <ModalComponent
            modalContent={
              <ProductForm
                productValues={productInfo}
                onEdit={(values: ProductProps) => {
                  // Handle edit logic here
                  setIsOpenModal(false);
                }}
                onCancel={() => {
                  setProductInfo(initialProductState);
                  setIsOpenModal(false);
                }}
              />
            }
          />
        )}
      </>
    );
  }

  return <ShowContent error={""} isLoading={false} content={content} />;
}
